import {
  HashLocationStrategy,
  LocationStrategy,
  registerLocaleData,
} from "@angular/common";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import locale from "@angular/common/locales/it";
import { APP_INITIALIZER, LOCALE_ID, NgModule } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { DateAdapter, MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import {
  MatPaginatorIntl,
  MatPaginatorModule,
} from "@angular/material/paginator";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSliderModule } from "@angular/material/slider";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSortModule } from "@angular/material/sort";
import { MatStepperModule } from "@angular/material/stepper";
import { MatTableModule } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
// Angular Material Components
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import {
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
  OWL_DATE_TIME_LOCALE,
} from "ng-pick-datetime";
import { MaterialFileInputModule } from "ngx-material-file-input";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";
import { SharedModule } from "src/app/shared.module";
import { ClickStopPropagationDirective } from "./click-stop-propagation.directive";
import { AppComponent } from "./component/app.component";
import { AreaSearchComponent } from "./component/area-search/area-search.component";
import { BadCredentialsComponent } from "./component/bad-credentials/bad-credentials.component";
import { ChangePasswordComponent } from "./component/change-password/change-password.component";
import { ConnectionInterruptedComponent } from "./component/connection-interrupted/connection-interrupted.component";
import { DeleteListComponent } from "./component/delete-list/delete-list.component";
import { DistributionListComponent } from "./component/distribution-list/distribution-list.component";
import { EditListComponent } from "./component/edit-list/edit-list.component";
import { ExtendMessageDialogComponent } from "./component/extend-message-dialog/extend-message-dialog.component";
import { FailedMessageSendingComponent } from "./component/failed-message-sending/failed-message-sending.component";
import { LoginComponent } from "./component/login/login.component";
import { MapDrawingRadiusToolComponent } from "./component/map-drawing-radius-tool/map-drawing-radius-tool.component";
import { NewListComponent } from "./component/new-list/new-list.component";
import { RefreshComponent } from "./component/refresh/refresh.component";
import { ResetPasswordComponent } from "./component/reset-password/reset-password.component";
import { App24PAMatPaginator } from "./component/show-message/customPaginatorIntl";
import { DetailMapByradiusComponent } from "./component/show-message/detail-map-byradius/detail-map-byradius.component";
import { DetailMapV2Component } from "./component/show-message/detail-map-v2/detail-map-v2.component";
import { DetailMapComponent } from "./component/show-message/detail-map/detail-map.component";
import { NewMessageComponent } from "./component/show-message/new-message/new-message.component";
import {
  NoSendersFoundDialog,
  ShowMessageComponent,
} from "./component/show-message/show-message.component";
import { SuccessMessageSendingComponent } from "./component/success-message-sending/success-message-sending.component";
import { TerminateMessageDialogComponent } from "./component/terminate-message-dialog/terminate-message-dialog.component";
import { TerminateResultDialogComponent } from "./component/terminate-result-dialog/terminate-result-dialog.component";
import { AuthGuard } from "./injectable/auth/auth.guard";
import { AuthInterceptor } from "./injectable/auth/auth.interceptor";
import { AuthService } from "./injectable/auth/auth.service";
import { DistributionListService } from "./injectable/distribution-list.service";
import { MessageService } from "./injectable/message.service";
import { SafePipe } from "./safe.pipe";
import { DialogErrorListComponent } from "./shared/components/dialog/dialog-error-list/dialog-error-list.component";
import { DialogGenericErrorResponseComponent } from "./shared/components/dialog/dialog-generic-error-response/dialog-generic-error-response.component";
import { DialogSentMessageComponent } from "./shared/components/dialog/dialog-sent-message/dialog-sent-message.component";
import { DialogComponent } from "./shared/components/dialog/dialog.component";
import { NotFoundComponent } from "./shared/components/not-found/not-found.component";
import { SuccessTerminatingSnackComponent } from "./success-terminating-snack/success-terminating-snack.component";
import { ShowClientsComponent } from "./component/show-clients/show-clients.component";
import { NgxsModule } from "@ngxs/store";
import { environment } from "src/environments/environment";
import { AppState } from "./app-state";
import { ClientDetailComponent } from "./component/client-detail/client-detail.component";
import { DeleteClientComponent } from "./component/show-clients/delete-client/delete-client.component";
import { EditSenderDialogComponent } from "./component/client-detail/edit-sender-dialog/edit-sender-dialog.component";
import { ShowUsersComponent } from "./component/show-users/show-users.component";
import { ShowAreasComponent } from "./component/show-areas/show-areas.component";
import { InsertUserDialogComponent } from "./component/show-users/insert-user-dialog/insert-user-dialog.component";
import { AreaDrawComponent } from "./component/area-draw/area-draw.component";
import { GenericErrorModalComponent } from "./injectable/generic-error-handling/generic-error-modal/generic-error-modal.component";
import { EditLogoDialogComponent } from "./component/client-detail/edit-logo-dialog/edit-logo-dialog.component";
import { InsertClientComponent } from "./component/show-clients/insert-client/insert-client.component";
import { InsertClientLogoComponent } from "./component/show-clients/insert-client-logo/insert-client-logo.component";
import { AreaInsertComponent } from "./component/area-insert/area-insert.component";
import { AreaInsertByGeoJsonComponent } from "./component/show-areas/area-insert-by-geo-json/area-insert-by-geo-json.component";
import { ModifyUserDialogComponent } from "./component/show-users/modify-user-dialog/modify-user-dialog.component";
import { ModifyUserSendersDialogComponent } from "./component/show-users/modify-user-senders-dialog/modify-user-senders-dialog.component";
import { InsertAreaDialogComponent } from "./component/area-insert/insert-area-dialog/insert-area-dialog.component";
import { AreaMapDialogComponent } from "./component/show-areas/area-map-dialog/area-map-dialog.component";
import {
  DialogContentDialog,
  ShowServicesComponent,
} from "./component/show-services/show-services.component";
import { ServiceModifyDialogComponent } from "./component/show-services/service-modify-dialog/service-modify-dialog.component";
import { InsertServiceComponent } from "./component/insert-service/insert-service.component";
import { ContactsComponent } from "./component/contacts/contacts.component";
import {
  AreaFactoryComponent,
  SingleAreaWarningDialog,
} from "./component/area-factory/area-factory.component";
import { MessageDetailComponent } from "./component/message-detail/message-detail.component";
import { FilterPipe } from "./injectable/filter.pipe";
import { ModifyServiceComponent } from "./component/modify-service/modify-service.component";
import { ModifyServiceState } from "./component/modify-service/modify-service-state";
import { ChartsModule } from "ng2-charts";
import { EnableDisableUserDialogComponent } from "./component/show-users/enable-disable-user-dialog/enable-disable-user-dialog.component";
import { CustomDateAdapter } from "./injectable/custom-date-adapter";
import { DeleteUserDialogComponent } from "./component/show-users/delete-user-dialog/delete-user-dialog.component";

registerLocaleData(locale);

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ShowMessageComponent,
    SafePipe,
    ResetPasswordComponent,
    ChangePasswordComponent,
    NotFoundComponent,
    DialogComponent,
    DialogSentMessageComponent,
    DialogGenericErrorResponseComponent,
    DialogErrorListComponent,
    DetailMapComponent,
    DistributionListComponent,
    DetailMapV2Component,
    MapDrawingRadiusToolComponent,
    DetailMapByradiusComponent,
    NewMessageComponent,
    SuccessMessageSendingComponent,
    FailedMessageSendingComponent,
    BadCredentialsComponent,
    TerminateResultDialogComponent,
    RefreshComponent,
    TerminateMessageDialogComponent,
    SuccessTerminatingSnackComponent,
    ExtendMessageDialogComponent,
    ConnectionInterruptedComponent,
    NewListComponent,
    ClickStopPropagationDirective,
    DeleteListComponent,
    AreaSearchComponent,
    EditListComponent,
    ShowClientsComponent,
    ClientDetailComponent,
    DeleteClientComponent,
    EditSenderDialogComponent,
    ShowUsersComponent,
    ShowAreasComponent,
    InsertUserDialogComponent,
    AreaDrawComponent,
    GenericErrorModalComponent,
    EditLogoDialogComponent,
    InsertClientComponent,
    InsertClientLogoComponent,
    AreaInsertComponent,
    AreaInsertByGeoJsonComponent,
    ModifyUserDialogComponent,
    ModifyUserSendersDialogComponent,
    InsertAreaDialogComponent,
    AreaMapDialogComponent,
    ShowServicesComponent,
    ServiceModifyDialogComponent,
    InsertServiceComponent,
    ContactsComponent,
    AreaFactoryComponent,
    MessageDetailComponent,
    SingleAreaWarningDialog,
    FilterPipe,
    ModifyServiceComponent,
    EnableDisableUserDialogComponent,
    DeleteUserDialogComponent,
    NoSendersFoundDialog,
    DialogContentDialog,
  ],

  imports: [
    SharedModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    NgbModule,
    NgxMaterialTimepickerModule.setLocale("it-IT"),
    MaterialFileInputModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatCheckboxModule,
    MatCheckboxModule,
    MatButtonModule,
    MatInputModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatRadioModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatGridListModule,
    MatCardModule,
    MatStepperModule,
    MatTabsModule,
    MatExpansionModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDialogModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    ChartsModule,
    NgxsModule.forRoot([AppState, ModifyServiceState], {
      developmentMode: !environment.production,
    }),
  ],

  entryComponents: [
    DialogComponent,
    DialogSentMessageComponent,
    DialogGenericErrorResponseComponent,
    DialogErrorListComponent,
  ],

  providers: [
    AuthGuard,
    AuthService,
    MatDatepickerModule,
    { provide: MatPaginatorIntl, useClass: App24PAMatPaginator },
    MessageService,
    DistributionListService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: LOCALE_ID, useValue: "it" },
    { provide: OWL_DATE_TIME_LOCALE, useValue: "it" },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],

  bootstrap: [AppComponent],
})
export class AppModule {}
